import { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Card, Chip, IconButton, MenuItem, Snackbar, Stack,
  Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";
import { useLanguage } from "../i18n/LanguageContext";
import { areasApi, bookingsApi } from "../api/resources";
import { COLORS } from "../theme";
import CalendarMonthView from "../components/areas/CalendarMonthView";
import CalendarWeekView from "../components/areas/CalendarWeekView";
import BookingDialog from "../components/areas/BookingDialog";
import AreaDialog from "../components/areas/AreaDialog";
import BookingsHistoryTable from "../components/areas/BookingsHistoryTable";
import { toISODate, weekDays, MONTH_LABELS } from "../utils/calendar";

const YEARS = (() => {
  const y = new Date().getFullYear();
  return [y - 1, y, y + 1, y + 2];
})();

export default function AreasComunesPage() {
  const { t, lang } = useLanguage();
  const [tab, setTab] = useState("reservaciones");
  const [areas, setAreas] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  const [selectedAreaId, setSelectedAreaId] = useState("");
  const selectedArea = areas.find((a) => a._id === selectedAreaId);

  const [calView, setCalView] = useState("month");
  const [anchorDate, setAnchorDate] = useState(new Date());

  const [bookingSlot, setBookingSlot] = useState(null); // { date, hour } | null
  const [editingBooking, setEditingBooking] = useState(null);
  const [areaDialogOpen, setAreaDialogOpen] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [toast, setToast] = useState(null);

  const loadAreas = () =>
    areasApi.list().then((list) => {
      setAreas(list);
      if (list.length && !selectedAreaId) setSelectedAreaId(list[0]._id);
    });

  const loadBookings = () => {
    if (!selectedAreaId) return;
    setLoadingBookings(true);
    bookingsApi.list({ areaId: selectedAreaId }).finally(() => setLoadingBookings(false)).then(setBookings);
  };

  useEffect(() => {
    loadAreas();
  }, []); // eslint-disable-line

  useEffect(() => {
    loadBookings();
  }, [selectedAreaId]); // eslint-disable-line

  const dialogOpen = !!bookingSlot || !!editingBooking;

  const shiftAnchor = (dir) => {
    const d = new Date(anchorDate);
    if (calView === "month") d.setMonth(d.getMonth() + dir);
    else if (calView === "week") d.setDate(d.getDate() + dir * 7);
    else d.setDate(d.getDate() + dir);
    setAnchorDate(d);
  };
  const setMonth = (m) => setAnchorDate(new Date(anchorDate.getFullYear(), m, 1));
  const setYear = (y) => setAnchorDate(new Date(y, anchorDate.getMonth(), 1));

  const handleSelectDay = (day) => {
    setAnchorDate(day);
    setCalView("day");
  };
  const handleSelectSlot = (day, hour) => setBookingSlot({ date: toISODate(day), hour });

  const handleSaveBooking = async (payload) => {
    if (editingBooking) {
      await bookingsApi.update(editingBooking._id, {
        occupantId: payload.occupantId,
        date: payload.date,
        startHour: payload.startHour,
        endHour: payload.endHour,
      });
      setToast({ severity: "success", message: t("areas.bookingUpdated", payload.occupant.name) });
    } else {
      const result = await bookingsApi.create({
        areaId: payload.areaId,
        occupantId: payload.occupantId,
        date: payload.date,
        startHour: payload.startHour,
        endHour: payload.endHour,
      });
      setToast({
        severity: result.notify?.sent ? "success" : "warning",
        message: t("areas.bookingSaved", payload.occupant.name),
      });
    }
    setBookingSlot(null);
    setEditingBooking(null);
    loadBookings();
  };

  const handleDeleteBooking = async (id) => {
    await bookingsApi.remove(id);
    setBookingSlot(null);
    setEditingBooking(null);
    setToast({ severity: "success", message: t("areas.bookingDeleted") });
    loadBookings();
  };

  const handleSaveArea = async (form) => {
    if (editingArea) await areasApi.update(editingArea._id, form);
    else await areasApi.create(form);
    setAreaDialogOpen(false);
    setEditingArea(null);
    loadAreas();
  };

  const currentDays = calView === "week" ? weekDays(anchorDate) : [anchorDate];

  return (
    <Box>
      <Typography variant="h5">{t("areas.title")}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("areas.subtitle")}
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="reservaciones" label={t("areas.tabCalendar")} />
        <Tab value="areas" label={t("areas.tabAreas")} />
        <Tab value="history" label={t("areas.tabHistory")} />
      </Tabs>

      {tab === "reservaciones" && (
        <Box>
          {areas.length === 0 ? (
            <Alert severity="info">{t("areas.noAreasYet")}</Alert>
          ) : (
            <>
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("areas.showingAvailabilityFor")}
                </Typography>
                <TextField
                  select
                  variant="standard"
                  value={selectedAreaId}
                  onChange={(e) => setSelectedAreaId(e.target.value)}
                  SelectProps={{ disableUnderline: true }}
                  sx={{ "& .MuiSelect-select": { fontFamily: "'IBM Plex Serif', serif", fontWeight: 600, fontSize: 17, color: COLORS.ink, py: 0 } }}
                >
                  {areas.map((a) => (
                    <MenuItem key={a._id} value={a._id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </TextField>
                {selectedArea?.fullDayOnly && <Chip size="small" label={t("areas.modeFullDay")} />}
              </Stack>

              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <ToggleButtonGroup size="small" value={calView} exclusive onChange={(_, v) => v && setCalView(v)}>
                  <ToggleButton value="month">{t("areas.viewMonth")}</ToggleButton>
                  <ToggleButton value="week">{t("areas.viewWeek")}</ToggleButton>
                  <ToggleButton value="day">{t("areas.viewDay")}</ToggleButton>
                </ToggleButtonGroup>

                <TextField select size="small" value={anchorDate.getMonth()} onChange={(e) => setMonth(Number(e.target.value))} sx={{ minWidth: 130 }}>
                  {MONTH_LABELS[lang].map((m, i) => (
                    <MenuItem key={m} value={i}>
                      {m}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField select size="small" value={anchorDate.getFullYear()} onChange={(e) => setYear(Number(e.target.value))} sx={{ minWidth: 90 }}>
                  {YEARS.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </TextField>

                <Stack direction="row" spacing={0.5}>
                  <IconButton size="small" onClick={() => shiftAnchor(-1)}>
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setAnchorDate(new Date())} title={t("areas.today")}>
                    <TodayIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => shiftAnchor(1)}>
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>

              {calView === "month" && (
                <CalendarMonthView anchorDate={anchorDate} bookings={bookings} onSelectDay={handleSelectDay} onSelectBooking={setEditingBooking} />
              )}
              {(calView === "week" || calView === "day") && (
                <CalendarWeekView
                  days={currentDays}
                  bookings={bookings}
                  isFullDay={!!selectedArea?.fullDayOnly}
                  onSelectSlot={handleSelectSlot}
                  onSelectBooking={setEditingBooking}
                />
              )}
            </>
          )}
        </Box>
      )}

      {tab === "areas" && (
        <Box>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingArea(null);
                setAreaDialogOpen(true);
              }}
            >
              {t("areas.addArea")}
            </Button>
          </Stack>
          <Stack spacing={1.5}>
            {areas.map((a) => (
              <Card key={a._id} sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontWeight={600}>{a.name}</Typography>
                      <Chip size="small" label={a.fullDayOnly ? t("areas.modeFullDay") : t("areas.modeHours")} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {a.description}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingArea(a);
                      setAreaDialogOpen(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Card>
            ))}
          </Stack>
        </Box>
      )}

      {tab === "history" && <BookingsHistoryTable areas={areas} />}

      <BookingDialog
        open={dialogOpen}
        area={selectedArea}
        date={bookingSlot?.date}
        initialHour={bookingSlot?.hour}
        existing={editingBooking}
        onClose={() => {
          setBookingSlot(null);
          setEditingBooking(null);
        }}
        onSave={handleSaveBooking}
        onDelete={handleDeleteBooking}
      />
      <AreaDialog open={areaDialogOpen} initial={editingArea} onClose={() => setAreaDialogOpen(false)} onSave={handleSaveArea} />

      <Snackbar open={!!toast} autoHideDuration={4500} onClose={() => setToast(null)}>
        {toast && (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
}
